import { resolveErrorMessage } from 'modules/errors';
import get from 'lodash/get';
import has from 'lodash/has';
import { Assets, uploadAssetActions } from 'modules/assets';
import { postNDA } from 'modules/ndas';
import {
  fetchAudition,
  fetchAuditions,
  fetchAllAuditions,
  postAudition,
  patchAudition,
  deleteAudition,
} from './api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isObjectLike } from 'lodash';

export const loadAudition = createAsyncThunk(
  'auditionsProducers/loadSingle',
  async ({ producerId, auditionId }) =>
    await fetchAudition(producerId, auditionId),
);

export const loadAllAuditions = createAsyncThunk(
  'auditionsProducers/loadAll',
  async (producerId) => await fetchAllAuditions(producerId),
);

export const loadAuditions = createAsyncThunk(
  'auditionsProducers/load',
  async ({ producerId, filter }) => await fetchAuditions(producerId, filter),
);

export const removeAudition = createAsyncThunk(
  'auditionsProducers/delete',
  async ({ producerId, auditionId }) => deleteAudition(producerId, auditionId),
);

export const createOrUpdateAudition = createAsyncThunk(
  'auditionsProducers/createAndUpdate',
  async ({ producerId, roleId, auditionId, values }) =>
    postOrPatchAudition(producerId, roleId, auditionId, values),
);

function uploadFiles(producerId, auditionId, values) {
  const scriptUrl = get(values, 'scriptUrl');
  const nonDisclosureAgreement = get(values, 'nonDisclosureAgreement');
  const additionalMediaUrl = get(values, 'additionalMediaUrl');

  const context = Assets.createAuditionContext(auditionId, producerId);

  const script = has(values, 'scriptUrl')
    ? isObjectLike(get(values, 'scriptUrl'))
      ? uploadAssetActions(scriptUrl, 'document', 'auditionScript', context)
      : get(values, 'scriptUrl')
    : null;

  const NDA = has(values, 'nonDisclosureAgreement')
    ? isObjectLike(get(values, 'nonDisclosureAgreement'))
      ? uploadAssetActions(
          nonDisclosureAgreement,
          'document',
          'auditionNonDisclosureAgreement',
          context,
        )
      : get(values, 'nonDisclosureAgreement')
    : null;

  const additional = has(values, 'additionalMediaUrl')
    ? isObjectLike(get(values, 'additionalMediaUrl'))
      ? uploadAssetActions(
          additionalMediaUrl,
          'media',
          'auditionAdditionalMedia',
          context,
        )
      : get(values, 'additionalMediaUrl')
    : null;

  return Promise.all([script, NDA, additional]);
}

const uploadAndUpdateAudition = (producerId, roleId, auditionId, values) => {
  const fileUpload = uploadFiles(producerId, auditionId, values);

  return fileUpload.then((responses) => {
    const scriptUrl = get(responses, '[0]');
    const ndaUrl = get(responses, '[1]');
    const additionalMediaUrl = get(responses, '[2]');

    return postNDA(auditionId, ndaUrl).then((newNDA) => {
      const audition = {
        ...values,
        id: auditionId,
        ...(has(values, 'nonDisclosureAgreement') && {
          ndaId: get(newNDA, 'id', null),
        }),
        ...(has(values, 'scriptUrl') && { scriptUrl }),
        ...(has(values, 'additionalMediaUrl') && { additionalMediaUrl }),
      };

      return patchAudition(producerId, roleId, audition);
    });
  });
};

const postOrPatchAudition = (producerId, roleId, auditionId, values) => {
  if (!auditionId) {
    return postAudition(producerId, roleId, null).then((newAudition) => {
      const newAuditionId = newAudition.data.id;
      const patchValues = { id: newAuditionId, ...values };

      return uploadAndUpdateAudition(
        producerId,
        roleId,
        newAuditionId,
        patchValues,
      );
    });
  }

  return uploadAndUpdateAudition(producerId, roleId, auditionId, values);
};

const auditionsProducersSlice = createSlice({
  name: 'auditionsProducers',
  initialState: {
    auditionsLoading: false,
    loading: false,
    deleting: false,
    error: false,
  },
  reducers: {
    removeAuditionAction(state) {
      state.audition = null;
    },
    removeErrorsAction(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAudition.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAudition.fulfilled, (state, _) => {
        state.loading = false;
      })
      .addCase(loadAudition.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
        state.loading = false;
      })
      .addCase(createOrUpdateAudition.fulfilled, (state, _) => {
        state.error = false;
      })
      .addCase(createOrUpdateAudition.rejected, (state, action) => {
        state.error = resolveErrorMessage(action.error);
      })
      .addCase(removeAudition.pending, (state, _) => {
        state.deleting = true;
      })
      .addCase(removeAudition.fulfilled, (state, _) => {
        state.deleting = false;
      })
      .addCase(removeAudition.rejected, (state, _) => {
        state.deleting = false;
      })
      .addCase(loadAuditions.pending, (state, _) => {
        state.auditionsLoading = true;
      })
      .addCase(loadAuditions.fulfilled, (state, _) => {
        state.auditionsLoading = false;
      })
      .addCase(loadAuditions.rejected, (state, _) => {
        state.auditionsLoading = false;
      })
      .addCase(loadAllAuditions.pending, (state, _) => {
        state.auditionsLoading = true;
      })
      .addCase(loadAllAuditions.fulfilled, (state, _) => {
        state.auditionsLoading = false;
      })
      .addCase(loadAllAuditions.rejected, (state, _) => {
        state.auditionsLoading = false;
      });
  },
  selectors: {
    getAuditionLoading: (state) => state.loading,
    getAuditionsLoading: (state) => state.auditionsLoading,
    getAuditionDeleting: (state) => state.deleting,
    getAuditionError: (state) => state.error,
  },
});

export const { removeAuditionAction, removeErrorsAction } =
  auditionsProducersSlice.actions;
export const reducer = auditionsProducersSlice.reducer;
export const {
  getAuditionLoading,
  getAuditionsLoading,
  getAuditionDeleting,
  getAuditionError,
} = auditionsProducersSlice.selectors;
